import { FormProvider, useForm } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { Button } from '@material-ui/core';
import _ from 'lodash';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { AdminPages, buildPageId } from '../AdminLinkUtils';
import FormSection from '../../form/FormSection';
import ListFormField from '../../form/ListFormField';
import {
    WAN_EXCLUDED_PATTERNS_FIELD,
    WAN_INCLUDED_PATTERNS_FIELD,
    WAN_INTERFACE_CONFIG_API_URL,
    WAN_INTERFACE_RULE_FIELD,
} from './WanInterfaceAdminConstants';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import WanRuleForm from './WanRuleForm';
import { ID_FIELD } from '../../form/FormConstants';
import { useWanHelperService } from '../sites/SiteAdminWanHelper';
import { usePlmPath } from '../../../utilities/PgmUtilities';

const functionName = AdminPages.WanInterfaces;
const goToUrl = '/admin/sites';
const defaultValues = {
    [WAN_INCLUDED_PATTERNS_FIELD]: [],
    [WAN_EXCLUDED_PATTERNS_FIELD]: [],
};

const ruleColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Rule`,
        sortable: false,
        mapping: WAN_INTERFACE_RULE_FIELD,
        flex: 1,
    },
];

const emptyGridText = i18n._(t`No rules defined`);
const breadCrumbText = i18n._(t`WAN Interface Rules`);
const addButtonText = i18n._(t`Create Rule`);

function prepareSavedData(dataToSave) {
    if (!dataToSave[WAN_INCLUDED_PATTERNS_FIELD] || dataToSave[WAN_INCLUDED_PATTERNS_FIELD].length === 0) {
        return {
            ...dataToSave,
            [WAN_INCLUDED_PATTERNS_FIELD]: [],
            [WAN_EXCLUDED_PATTERNS_FIELD]: [],
        };
    }

    return {
        ...dataToSave,
        [WAN_INCLUDED_PATTERNS_FIELD]: _.map(
            dataToSave[WAN_INCLUDED_PATTERNS_FIELD],
            (rule) => rule[WAN_INTERFACE_RULE_FIELD]
        ),
        [WAN_EXCLUDED_PATTERNS_FIELD]: _.map(
            dataToSave[WAN_EXCLUDED_PATTERNS_FIELD],
            (rule) => rule[WAN_INTERFACE_RULE_FIELD]
        ),
    };
}

export default function WanInterfaceAdminDetail() {
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues });
    const { reset, setError, handleSubmit, watch } = methods;
    const breadcrumbs = [{ text: breadCrumbText }];
    const inclusionRules = watch(WAN_INCLUDED_PATTERNS_FIELD);
    const wanHelperService = useWanHelperService();
    const formSaveService = useFormSaveService();
    const returnUrl = usePlmPath(goToUrl);

    const postFindHandler = (response) => {
        const cleaned = _.cloneDeep(response) || {};
        cleaned[WAN_INCLUDED_PATTERNS_FIELD] = _.map(cleaned[WAN_INCLUDED_PATTERNS_FIELD], (pattern) => ({
            [WAN_INTERFACE_RULE_FIELD]: pattern,
        }));
        cleaned[WAN_EXCLUDED_PATTERNS_FIELD] = _.map(cleaned[WAN_EXCLUDED_PATTERNS_FIELD], (pattern) => ({
            [WAN_INTERFACE_RULE_FIELD]: pattern,
        }));

        return cleaned;
    };

    const { initialized, finding, formData } = useFind({
        url: WAN_INTERFACE_CONFIG_API_URL,
        postFindHandler,
        queryFunc: wanHelperService.queryWanInterfaceConfig,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}),
    });

    const handleFormSubmit = (dataToSave = {}) => {
        const putData = prepareSavedData(dataToSave);
        return formSaveService.save({
            recordId: formData[ID_FIELD],
            url: WAN_INTERFACE_CONFIG_API_URL,
            dataToSave: putData,
            setError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit, returnUrl);

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={buildPageId(functionName)}
            actions={
                <Actions>
                    <Button onClick={handleSubmit(handleSubmitFunction)} variant="contained" color="primary">
                        <Trans>Save</Trans>
                    </Button>
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm submitFunction={handleSubmitFunction} initialized={initialized} loading={finding || saving}>
                    <FormSection
                        title={t`Global Inclusion Rules`}
                        subText={t`For each site, any interface where the name or description contains any of the inclusion rule strings will be automatically added as a WAN interface`}
                    >
                        <ListFormField
                            fieldName={WAN_INCLUDED_PATTERNS_FIELD}
                            dbSelected={_.get(formData, WAN_INCLUDED_PATTERNS_FIELD)}
                            addButtonText={addButtonText}
                            gridColumns={ruleColumns}
                            emptyGridText={emptyGridText}
                            formComponent={<WanRuleForm />}
                        />
                    </FormSection>

                    {inclusionRules && inclusionRules.length > 0 && (
                        <FormSection
                            title={t`Global Exclusion Rules`}
                            subText={t`For each site, any interface where the name or description contains any of the exclusion rule strings will not be added as a WAN interface. This supersedes inclusion rules`}
                        >
                            <ListFormField
                                fieldName={WAN_EXCLUDED_PATTERNS_FIELD}
                                dbSelected={_.get(formData, WAN_EXCLUDED_PATTERNS_FIELD)}
                                addButtonText={addButtonText}
                                gridColumns={ruleColumns}
                                emptyGridText={emptyGridText}
                                formComponent={<WanRuleForm />}
                            />
                        </FormSection>
                    )}
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}
